import { debounce } from 'throttle-debounce';

import {
  debugLog,
  DisplayPlacementId,
  UNLEASH_FEATURE_NAME
} from '@schibsted-nmp/advertising-shared';
import { $config, $waitForClient } from '@client/core/atoms/config.js';
import { addLifecycleEvent } from '@client/core/atoms/metrics.js';
import { isFeatureEnabled } from '@client/core/atoms/unleashFeatures.js';
import * as RelevantDigital from '@client/core/relevantDigital/index.js';
import { debouncedSendMetrics } from '@client/core/services/metrics.js';

import { Api } from './Api/index.js';
import { isLowPerformingSubverticalSection } from './utils/shouldLoadRelevantDigitalSlots.js';

export namespace BatchedSlots {
  let queue = new Array<googletag.Slot>();
  let normalQueue = new Array<googletag.Slot>();
  let immediateQueue = new Array<googletag.Slot>();

  export const IMMEDIATE_SLOTS = [
    DisplayPlacementId.AdvtTop1,
    DisplayPlacementId.AdvtRight1,
    DisplayPlacementId.AdvtLeft1,
    DisplayPlacementId.AdvtWallpaper,
    DisplayPlacementId.AdvtAudience
  ];

  export namespace SplitBatchQueue {
    export function add(slot: googletag.Slot) {
      const slotId = slot.getSlotElementId();
      const isImmediatePlacement = IMMEDIATE_SLOTS.includes(
        slotId as DisplayPlacementId
      );

      if (isImmediatePlacement) {
        immediateQueue.push(slot);
        SplitBatchImmediateQueue.flush.debounced();
      } else {
        normalQueue.push(slot);
        SplitBatchQueue.flush.debounced();
      }

      const waitForClient = $waitForClient.get();
      if (waitForClient) {
        $waitForClient.subscribe((wait) => {
          if (!wait) {
            if (isImmediatePlacement && immediateQueue.length > 0) {
              SplitBatchImmediateQueue.flush.debounced();
            } else if (!isImmediatePlacement && normalQueue.length > 0) {
              SplitBatchQueue.flush.debounced();
            }
          }
        });
      }
    }

    export function flush() {
      const slotsToLoad = [...normalQueue];
      normalQueue = [];

      load(slotsToLoad).catch((error: unknown) => {
        debugLog('Failed batched loading of slots', { slotsToLoad, error });
      });
    }

    export namespace flush {
      /** Debounce function that accumulates slots and refreshes them after 200ms. */
      export const debounced = debounce(200, SplitBatchQueue.flush, {
        atBegin: false
      });
    }
  }

  export namespace SplitBatchImmediateQueue {
    export function flush() {
      const slotsToLoad = [...immediateQueue];
      immediateQueue = [];

      load(slotsToLoad).catch((error: unknown) => {
        debugLog('Failed immediate loading of slots', { slotsToLoad, error });
      });
    }

    export namespace flush {
      /** Debounce function that accumulates immediate slots and refreshes them after 50ms. */
      export const debounced = debounce(50, SplitBatchImmediateQueue.flush, {
        atBegin: false
      });
    }
  }

  export namespace Queue {
    export function add(slot: googletag.Slot) {
      queue.push(slot);
      flush.debounced();

      const waitForClient = $waitForClient.get();
      if (waitForClient) {
        $waitForClient.subscribe((wait) => {
          if (!wait) flush.debounced();
        });
      }
    }

    export function flush() {
      const slotsToLoad = [...queue];
      queue = [];

      load(slotsToLoad).catch((error: unknown) => {
        debugLog('Failed batched loading of slots', { slotsToLoad, error });
      });
    }

    export namespace flush {
      /** Debounce function that accumulates slots and refreshes them after 200ms. */
      export const debounced = debounce(200, Queue.flush, { atBegin: false });
    }
  }

  export async function load(
    slots: Array<googletag.Slot>,
    ...abortSignals: ReadonlyArray<AbortSignal>
  ) {
    const { brand, subvertical, pageType } = $config.get() ?? {};
    const formattedSlotIds = await Api(
      (googletag) => {
        for (const slot of slots) {
          googletag.display(slot);
        }

        return slots.map((slot) => slot.getSlotElementId()).join(', ');
      },
      ...abortSignals
    );

    if (
      isFeatureEnabled(UNLEASH_FEATURE_NAME.enableGamPrebid) &&
      !isLowPerformingSubverticalSection(brand, pageType, subvertical)
    ) {
      try {
        await RelevantDigital.Slots.load(slots);
        addLifecycleEvent(`Prebid auction completed for ${formattedSlotIds}`);
      } catch (error) {
        debugLog('Prebid auction failed', error);
        addLifecycleEvent(`Prebid auction failed for ${formattedSlotIds}`);
      }
    }

    await Api(...abortSignals)
      .pubads()
      .refresh(slots);

    addLifecycleEvent(`GAM Load batch of Ads for ${formattedSlotIds}`);
    debouncedSendMetrics();
  }
}
